import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    LockClosedIcon,
    EyeIcon,
    EyeSlashIcon,
    UserIcon,
    EnvelopeIcon,
    PhoneIcon,
    CameraIcon,
    CheckCircleIcon,
    ArrowPathIcon,
    DocumentTextIcon
} from "@heroicons/react/24/outline";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import toast from "react-hot-toast";
import useAddressStore from "../../store/useAddressStore";
import useConsultantAuthStore from "../../store/consultant/useConsultantAuthStore";
import { axiosInstance } from "../../lib/axiosInstance";

const ConsultantSignup = () => {
    const [formData, setFormData] = useState({
        engineerName: "",
        email: "",
        contactNumber: "",
        password: "",
        district: "",
        taluk: "",
        villageName: "",
        pincode: "",
        qualification: "",
        qualificationCustom: "",
        experience: "",
        engineerPhoto: null,
        emailOtp: "",
        aadhaarNumber: "",
        aadhaarOtp: "",
        aadhaarRefId: "",
        aadhaarVerified: false,
        qualificationDocument: null,
    });

    const [showPassword, setShowPassword] = useState(false);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [showCustomQualification, setShowCustomQualification] = useState(false);
    const [emailVerified, setEmailVerified] = useState(false);
    const [showEmailOtp, setShowEmailOtp] = useState(false);
    const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
    const [isVerifyingAadhaar, setIsVerifyingAadhaar] = useState(false);
    const [isVerifyingAadhaarOtp, setIsVerifyingAadhaarOtp] = useState(false);
    const [showAadhaarOtpInput, setShowAadhaarOtpInput] = useState(false);

    const {
        districts,
        taluks,
        fetchMappedDistricts,
        fetchMappedTaluks,
        isLoadingDistricts,
        isLoadingTaluks,
    } = useAddressStore();

    const { signup, sendEmailOtp, verifyEmailOtp, isLoading } = useConsultantAuthStore();
    const navigate = useNavigate();

    useEffect(() => {
        fetchMappedDistricts("Tamil Nadu");
    }, [fetchMappedDistricts]);

    useEffect(() => {
        if (formData.district) {
            fetchMappedTaluks(formData.district, "Tamil Nadu");
        } else {
            useAddressStore.setState({ taluks: [] });
        }
    }, [formData.district, fetchMappedTaluks]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Handle qualification selection
        if (name === "qualification") {
            if (value === "Others") {
                setShowCustomQualification(true);
                setFormData({ ...formData, qualification: value, qualificationCustom: "" });
            } else {
                setShowCustomQualification(false);
                setFormData({ ...formData, qualification: value, qualificationCustom: "" });
            }
            return;
        }

        // Clear taluk when district changes
        if (name === "district") {
            setFormData({ ...formData, district: value, taluk: "" });
            return;
        }

        // Restrict numerical fields to digits and length
        if (name === "contactNumber" && value.length > 10) return;
        if (name === "pincode" && value.length > 6) return;
        if (name === "emailOtp" && value.length > 6) return;
        if (name === "aadhaarNumber" && value.length > 12) return;
        if (name === "aadhaarOtp" && value.length > 6) return;
        if (
            (name === "contactNumber" ||
                name === "pincode" ||
                name === "emailOtp" ||
                name === "aadhaarNumber" ||
                name === "aadhaarOtp") &&
            !/^\d*$/.test(value)
        ) {
            return;
        }

        // If Aadhaar changes after verification, reset Aadhaar-related flags
        if (name === "aadhaarNumber" && formData.aadhaarVerified) {
            setFormData({
                ...formData,
                aadhaarNumber: value,
                aadhaarVerified: false,
                aadhaarRefId: "",
                aadhaarOtp: "",
            });
            setShowAadhaarOtpInput(false);
            return;
        }

        setFormData({ ...formData, [name]: value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
            const maxSize = 2 * 1024 * 1024;
            if (!allowedTypes.includes(file.type)) {
                toast.error("Invalid photo type. JPG, PNG, WEBP only.");
                e.target.value = null;
                return;
            }
            if (file.size > maxSize) {
                toast.error("Photo size exceeds 2MB.");
                e.target.value = null;
                return;
            }
            setFormData({ ...formData, engineerPhoto: file });
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDocumentChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
            const maxSize = 5 * 1024 * 1024;
            if (!allowedTypes.includes(file.type)) {
                toast.error("Invalid document type. PDF, JPG, PNG only.");
                e.target.value = null;
                return;
            }
            if (file.size > maxSize) {
                toast.error("Document size exceeds 5MB.");
                e.target.value = null;
                return;
            }
            setFormData({ ...formData, qualificationDocument: file });
            toast.success("Document attached: " + file.name);
        }
    };
    const verifyAadhaar = async () => {
        if (!formData.aadhaarNumber || formData.aadhaarNumber.length !== 12) {
            toast.error("Please enter a valid 12-digit Aadhaar number.");
            return;
        }

        setIsVerifyingAadhaar(true);
        try {
            const response = await axiosInstance.post("/builders/auth/aadhaar/generate-otp", {
                aadhaarNumber: formData.aadhaarNumber,
            });

            if (response.data?.ref_id) {
                setFormData((prev) => ({
                    ...prev,
                    aadhaarRefId: response.data.ref_id,
                }));
                setShowAadhaarOtpInput(true);
                toast.success(response.data.message || "OTP sent successfully.");
            } else {
                toast.error(response.data?.message || "Failed to send Aadhaar OTP.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Aadhaar verification request failed.";
            toast.error(errorMsg);
        } finally {
            setIsVerifyingAadhaar(false);
        }
    };

    const verifyAadhaarOtp = async () => {
        if (!formData.aadhaarOtp || formData.aadhaarOtp.length !== 6) {
            toast.error("Please enter the 6-digit OTP.");
            return;
        }

        setIsVerifyingAadhaarOtp(true);
        try {
            const response = await axiosInstance.post("/builders/auth/aadhaar/verify-otp", {
                otp: formData.aadhaarOtp,
                ref_id: formData.aadhaarRefId,
                aadhaarNumber: formData.aadhaarNumber,
            });

            if (response.data?.message === "Aadhaar verified successfully." && response.data?.details) {
                const details = response.data.details;
                setFormData((prev) => ({
                    ...prev,
                    aadhaarVerified: true,
                    engineerName: prev.engineerName || details.name || "",
                }));
                setShowAadhaarOtpInput(false);
                toast.success("Aadhaar verified successfully!");
            } else {
                toast.error(response.data?.message || "Invalid OTP or verification failed.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Aadhaar OTP verification failed.";
            toast.error(errorMsg);
        } finally {
            setIsVerifyingAadhaarOtp(false);
        }
    };

    const handleSendOtp = async () => {
        if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
            toast.error("Please enter a valid email address.");
            return;
        }
        setIsVerifyingEmail(true);
        const success = await sendEmailOtp(formData.email);
        if (success) {
            setShowEmailOtp(true);
        }
        setIsVerifyingEmail(false);
    };

    const handleVerifyOtp = async () => {
        if (!formData.emailOtp || formData.emailOtp.length !== 6) {
            toast.error("Please enter the 6-digit OTP.");
            return;
        }
        setIsVerifyingEmail(true);
        const success = await verifyEmailOtp(formData.email, formData.emailOtp);
        if (success) {
            setEmailVerified(true);
            setShowEmailOtp(false);
        }
        setIsVerifyingEmail(false);
    };

    const handleSignup = async (e) => {
        e.preventDefault();

        // Email verification check
        if (!emailVerified) {
            toast.error("Please verify your email address.");
            return;
        }

        // Aadhaar verification check
        if (!formData.aadhaarVerified) {
            toast.error("Please verify your Aadhaar number.");
            return;
        }

        // Validation
        if (!formData.engineerPhoto) {
            toast.error("Please upload your photo.");
            return;
        }

        if (!formData.qualificationDocument) {
            toast.error("Please upload your qualification document (PDF/Image).");
            return;
        }

        if (formData.contactNumber.length !== 10) {
            toast.error("Contact number must be 10 digits.");
            return;
        }

        if (formData.pincode.length !== 6) {
            toast.error("Pincode must be 6 digits.");
            return;
        }

        if (formData.qualification === "Others" && !formData.qualificationCustom.trim()) {
            toast.error("Please specify your qualification.");
            return;
        }

        // Prepare data for submission
        const submitData = {
            engineerName: formData.engineerName,
            email: formData.email,
            contactNumber: formData.contactNumber,
            password: formData.password,
            district: formData.district,
            taluk: formData.taluk,
            villageName: formData.villageName,
            pincode: formData.pincode,
            qualification: formData.qualification === "Others" ? formData.qualificationCustom : formData.qualification,
            experience: formData.experience,
            engineerPhoto: formData.engineerPhoto,
            qualificationDocument: formData.qualificationDocument,
            // Send Aadhaar under multiple keys so backend can store it
            aadhaarNumber: formData.aadhaarNumber,
            aadhar_number: formData.aadhaarNumber,
        };

        const success = await signup(submitData);
        if (success) {
            navigate("/consultant/dashboard");
        }
    };

    const qualificationOptions = [
        "",
        "BE - Civil",
        "B.Arch",
        "ME - Civil",
        "M.Arch",
        "Others"
    ];

    return (
        <div
            className="min-h-screen flex items-center justify-center p-4"
            style={{
                backgroundImage: "url('/assets/Profile Bg.jpg')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
            }}
        >
            <div
                className="flex flex-col w-full max-w-4xl p-6 md:p-8 border bg-white rounded-[40px] shadow-lg overflow-hidden my-8"
            >
                <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-700 uppercase">
                        Consultant Registration
                    </h2>
                    <p className="text-lg font-medium text-black mt-2">
                        <span className="text-black font-poppins font-bold">Join Ducktail</span>
                        {" - "}
                        <span className="text-blue-500 font-poppins font-bold">Get more Projects</span>
                    </p>
                    <p className="text-sm text-gray-500">
                        Connect with real clients looking for certified consultants nearby
                    </p>
                </div>

                <form className="w-full" onSubmit={handleSignup}>
                    {/* 3-column grid matching builder signup */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">

                        {/* Aadhaar Number */}
                        <div className="mb-1">
                            <label className="text-gray-900 text-sm font-medium mb-1 block">
                                Aadhaar Number <span className="text-red-500">*</span>
                            </label>
                            <div className="flex items-center">
                                <div className="relative w-full flex items-center">
                                    <input
                                        type="text"
                                        name="aadhaarNumber"
                                        value={formData.aadhaarNumber}
                                        onChange={handleInputChange}
                                        className="w-full py-2 px-2.5 border border-gray-700 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                        placeholder="Enter Aadhaar Number"
                                        maxLength={12}
                                        required
                                        disabled={isLoading || formData.aadhaarVerified}
                                        inputMode="numeric"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={verifyAadhaar}
                                    disabled={isVerifyingAadhaar || !formData.aadhaarNumber || formData.aadhaarNumber.length !== 12 || formData.aadhaarVerified}
                                    className={`ml-2 px-3 py-2 rounded-lg text-sm flex items-center justify-center min-w-[60px] ${formData.aadhaarVerified ? "bg-green-500 text-white cursor-not-allowed" : "bg-blue-500 text-white hover:bg-blue-600"} transition-colors`}
                                >
                                    {isVerifyingAadhaar ? <ArrowPathIcon className="w-5 h-5 animate-spin" /> : formData.aadhaarVerified ? "Verified" : "Verify"}
                                </button>
                            </div>
                        </div>

                        {/* Aadhaar OTP (conditionally shown) */}
                        {showAadhaarOtpInput && !formData.aadhaarVerified && (
                            <div className="mb-1">
                                <label className="text-gray-900 text-sm font-medium mb-1 block">
                                    Aadhaar OTP <span className="text-red-500">*</span>
                                </label>
                                <div className="flex items-center">
                                    <input
                                        type="text"
                                        name="aadhaarOtp"
                                        value={formData.aadhaarOtp}
                                        onChange={handleInputChange}
                                        className="w-full py-2 px-2.5 border border-gray-700 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                        placeholder="Enter 6-digit OTP"
                                        maxLength={6}
                                        inputMode="numeric"
                                    />
                                    <button
                                        type="button"
                                        onClick={verifyAadhaarOtp}
                                        disabled={isVerifyingAadhaarOtp || !formData.aadhaarOtp}
                                        className="ml-2 px-3 py-2 rounded-lg text-sm flex items-center justify-center min-w-[60px] bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                                    >
                                        {isVerifyingAadhaarOtp ? <ArrowPathIcon className="w-5 h-5 animate-spin" /> : "Verify"}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Qualification Document Upload */}
                        <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-900 mb-1.5 flex items-center">
                                Qualification Doc (PDF/IMG) <span className="text-red-500 ml-1">*</span>
                            </label>
                            <div className="flex items-center gap-3">
                                <div className={`relative flex-1 group ${formData.qualificationDocument ? 'bg-blue-50 border-blue-400' : 'bg-gray-50 border-gray-700'} border border-dashed rounded-[12px] h-[42px] flex items-center px-3 transition-all`}>
                                    <div className="flex items-center gap-2 overflow-hidden">
                                        <DocumentTextIcon className={`w-5 h-5 flex-shrink-0 ${formData.qualificationDocument ? 'text-blue-600' : 'text-gray-400'}`} />
                                        <span className={`text-sm truncate ${formData.qualificationDocument ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>
                                            {formData.qualificationDocument ? formData.qualificationDocument.name : "Upload Proof (Max 5MB)"}
                                        </span>
                                    </div>
                                    <input
                                        type="file"
                                        onChange={handleDocumentChange}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        disabled={isLoading}
                                    />
                                </div>
                                {formData.qualificationDocument && (
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, qualificationDocument: null })}
                                        className="bg-red-50 text-red-500 p-2 rounded-full hover:bg-red-100 transition-colors"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Email */}
                        <div className="mb-1">
                            <label className="text-gray-900 text-sm font-medium mb-1 block">
                                Email ID <span className="text-red-500">*</span>
                            </label>
                            <div className="flex items-center">
                                <div className="relative w-full flex items-center">
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={`w-full py-2 px-2.5 border ${emailVerified ? 'border-green-500 bg-green-50' : 'border-gray-700'} rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm`}
                                        placeholder="Enter Email"
                                        required
                                        disabled={isLoading || emailVerified}
                                    />
                                    {emailVerified && <CheckCircleIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />}
                                </div>
                                {!emailVerified && !showEmailOtp && (
                                    <button
                                        type="button"
                                        onClick={handleSendOtp}
                                        disabled={isVerifyingEmail || !formData.email}
                                        className={`ml-2 px-3 py-2 rounded-lg text-sm flex items-center justify-center min-w-[60px] ${emailVerified ? "bg-green-500 text-white cursor-not-allowed" : "bg-blue-500 text-white hover:bg-blue-600"} transition-colors`}
                                    >
                                        {isVerifyingEmail ? <ArrowPathIcon className="w-5 h-5 animate-spin" /> : "Verify"}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Email OTP (conditionally shown) */}
                        {showEmailOtp && !emailVerified && (
                            <div className="mb-1">
                                <label className="text-gray-900 text-sm font-medium mb-1 block">
                                    Email OTP <span className="text-red-500">*</span>
                                </label>
                                <div className="flex items-center">
                                    <input
                                        type="text"
                                        name="emailOtp"
                                        value={formData.emailOtp}
                                        onChange={handleInputChange}
                                        className="w-full py-2 px-2.5 border border-gray-700 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                        placeholder="Enter 6-digit OTP"
                                        maxLength={6}
                                        inputMode="numeric"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleVerifyOtp}
                                        disabled={isVerifyingEmail || !formData.emailOtp}
                                        className="ml-2 px-3 py-2 rounded-lg text-sm flex items-center justify-center min-w-[60px] bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                                    >
                                        {isVerifyingEmail ? <ArrowPathIcon className="w-5 h-5 animate-spin" /> : "Verify"}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Engineer Name */}
                        <div className="mb-1">
                            <label className="text-gray-900 text-sm font-medium mb-1 block">
                                Engineer Name <span className="text-red-500">*</span>
                            </label>
                            <div className="relative w-full flex items-center">
                                <span className="absolute left-2.5 flex items-center pointer-events-none">
                                    <UserIcon className="w-5 h-5 text-gray-500" />
                                </span>
                                <input
                                    type="text"
                                    name="engineerName"
                                    value={formData.engineerName}
                                    onChange={handleInputChange}
                                    className="w-full pl-9 pr-2.5 py-2 border border-gray-700 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                    placeholder="Enter Engineer Name"
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        {/* Contact Number */}
                        <div className="mb-1">
                            <label className="text-gray-900 text-sm font-medium mb-1 block">
                                Contact Number <span className="text-red-500">*</span>
                            </label>
                            <div className="relative w-full flex items-center">
                                <span className="absolute left-2.5 flex items-center pointer-events-none">
                                    <PhoneIcon className="w-5 h-5 text-gray-500" />
                                </span>
                                <input
                                    type="tel"
                                    name="contactNumber"
                                    value={formData.contactNumber}
                                    onChange={handleInputChange}
                                    className="w-full pl-9 pr-2.5 py-2 border border-gray-700 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                    placeholder="10 digit number"
                                    required
                                    disabled={isLoading}
                                    maxLength={10}
                                    inputMode="numeric"
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div className="mb-1">
                            <label className="text-gray-900 text-sm font-medium mb-1 block">
                                Password <span className="text-red-500">*</span>
                            </label>
                            <div className="relative w-full flex items-center">
                                <span className="absolute left-2.5 flex items-center pointer-events-none">
                                    <LockClosedIcon className="w-5 h-5 text-gray-500" />
                                </span>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className="w-full pl-9 pr-9 py-2 border border-gray-700 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                    placeholder="Create Password"
                                    required
                                    disabled={isLoading}
                                    minLength={8}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-2.5 inset-y-0 flex items-center text-gray-500 hover:text-gray-700"
                                >
                                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                </button>
                            </div>
                        </div>

                        {/* District */}
                        <div className="mb-1">
                            <label className="text-gray-900 text-sm font-medium mb-1 block">
                                District <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="district"
                                value={formData.district}
                                onChange={handleInputChange}
                                className="w-full py-2 px-2.5 border border-gray-700 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                required
                                disabled={isLoading || isLoadingDistricts}
                            >
                                <option value="">{isLoadingDistricts ? "Loading..." : "Select District"}</option>
                                {districts?.map((d) => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>

                        {/* Taluk */}
                        <div className="mb-1">
                            <label className="text-gray-900 text-sm font-medium mb-1 block">
                                Taluk <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="taluk"
                                value={formData.taluk}
                                onChange={handleInputChange}
                                className="w-full py-2 px-2.5 border border-gray-700 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                required
                                disabled={isLoading || !formData.district || isLoadingTaluks}
                            >
                                <option value="">{isLoadingTaluks ? "Loading..." : "Select Taluk"}</option>
                                {taluks?.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>

                        {/* Village Name */}
                        <div className="mb-1">
                            <label className="text-gray-900 text-sm font-medium mb-1 block">
                                Village Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="villageName"
                                value={formData.villageName}
                                onChange={handleInputChange}
                                className="w-full py-2 px-2.5 border border-gray-700 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                placeholder="Enter Village Name"
                                required
                                disabled={isLoading}
                            />
                        </div>

                        {/* Pincode */}
                        <div className="mb-1">
                            <label className="text-gray-900 text-sm font-medium mb-1 block">
                                Pincode <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="pincode"
                                value={formData.pincode}
                                onChange={handleInputChange}
                                className="w-full py-2 px-2.5 border border-gray-700 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                placeholder="6 digit pincode"
                                required
                                disabled={isLoading}
                                maxLength={6}
                                inputMode="numeric"
                            />
                        </div>

                        {/* Qualification */}
                        <div className="mb-1">
                            <label className="text-gray-900 text-sm font-medium mb-1 block">
                                Qualification <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="qualification"
                                value={formData.qualification}
                                onChange={handleInputChange}
                                className="w-full py-2 px-2.5 border border-gray-700 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                required
                                disabled={isLoading}
                            >
                                {qualificationOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option === "" ? "Select Qualification" : option}
                                    </option>
                                ))}
                            </select>
                            {showCustomQualification && (
                                <input
                                    type="text"
                                    name="qualificationCustom"
                                    value={formData.qualificationCustom}
                                    onChange={handleInputChange}
                                    className="mt-2 w-full py-2 px-2.5 border border-blue-300 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm bg-blue-50"
                                    placeholder="Type Course Name"
                                    required={formData.qualification === "Others"}
                                    disabled={isLoading}
                                />
                            )}
                        </div>

                        {/* Experience */}
                        <div className="mb-1">
                            <label className="text-gray-900 text-sm font-medium mb-1 block">
                                Experience (years) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="experience"
                                value={formData.experience}
                                onChange={handleInputChange}
                                className="w-full py-2 px-2.5 border border-gray-700 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                placeholder="e.g., 5"
                                required
                                disabled={isLoading}
                            />
                        </div>

                        {/* Engineer Photo */}
                        <div className="mb-1">
                            <label className="text-gray-900 text-sm font-medium mb-1 block">
                                Engineer Photo <span className="text-red-500">*</span>
                            </label>
                            <div className="flex items-center gap-3">
                                <div className="w-16 h-16 border-2 border-dashed border-gray-400 rounded-full flex items-center justify-center overflow-hidden relative cursor-pointer hover:border-blue-500 flex-shrink-0">
                                    {photoPreview ? (
                                        <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                                    ) : (
                                        <CameraIcon className="w-8 h-8 text-gray-400" />
                                    )}
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/jpg,image/webp"
                                        onChange={handleFileChange}
                                        className="opacity-0 absolute inset-0 cursor-pointer"
                                        disabled={isLoading}
                                    />
                                </div>
                                <p className="text-sm text-gray-500">JPG, PNG, WEBP<br />Max 2MB</p>
                            </div>
                        </div>

                    </div>

                    {/* Submit Button */}
                    <div className="flex flex-col items-center pt-6 gap-2">
                        <button
                            type="submit"
                            disabled={isLoading || !emailVerified}
                            className={`w-40 px-4 py-2 text-white transition-all duration-300 flex items-center justify-center rounded-md text-sm font-semibold shadow-sm ${isLoading || !emailVerified ? "opacity-70 cursor-not-allowed bg-blue-400" : "bg-blue-600 hover:bg-blue-700"}`}
                        >
                            {isLoading ? (
                                <AiOutlineLoading3Quarters className="animate-spin text-lg" />
                            ) : (
                                "REGISTER"
                            )}
                        </button>
                    </div>
                </form>

                {/* Already have an account */}
                <div className="mt-6 text-sm text-gray-600 text-center">
                    <span>Already have an account? </span>
                    <button
                        onClick={() => navigate("/consultant/login")}
                        className="ml-1 font-semibold text-blue-600 hover:underline"
                        disabled={isLoading}
                    >
                        Login
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConsultantSignup;
